import React from 'react';
import {
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormGroup,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import AdminFormRow from '../../../../components/admin/AdminFormRow';
import AdminMultiValueAutocomplete from '../../../../components/admin/AdminMultiValueAutocomplete';
import AdminSectionHeading from '../../../../components/admin/AdminSectionHeading';
import type { StorePosMappingOptionsResponse } from '../../../../services/storePosMappingOptionsService';
import {
    adminCheckboxSx,
    adminCombineSx,
    adminDividerSx,
    adminFormControlSx,
    adminFormFieldSx,
    adminInlineControlLabelSx,
} from '../../../../styles/adminComponentStyles';
import type { StoreFormData } from '../../../../types/features/storeDetails/storeForm';
import { formatPosTypeLabel } from '../../utils/posTypeLabels';
import { COUNTER_SCANNING_METHOD_OPTIONS } from '../utils/storeDetailsStoreFormUtils';

const DEFAULT_BACKSTOCK_AUDIT_FREQUENCY_SECONDS = 24 * 60 * 60;

type StoreDetailsStoreFormFieldsProps = {
    control: Control<StoreFormData>;
    countries: string[];
    errors: FieldErrors<StoreFormData>;
    mappingOptions: StorePosMappingOptionsResponse;
    posTypes: string[];
    selectedCountry: string;
    statesByCountry: Record<string, string[]>;
};

const StoreDetailsStoreFormFields: React.FC<StoreDetailsStoreFormFieldsProps> = ({
    control,
    countries,
    errors,
    mappingOptions,
    posTypes,
    selectedCountry,
    statesByCountry,
}) => (
    <Stack spacing={1.25}>
        <AdminSectionHeading>Store Information</AdminSectionHeading>
        <AdminFormRow label="Store Name" required>
            <Controller
                name="name"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        placeholder="Store name"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        inputProps={{ 'aria-label': 'Store Name' }}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Address Line 1" required>
            <Controller
                name="address_line1"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        placeholder="Address line 1"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.address_line1}
                        helperText={errors.address_line1?.message}
                        inputProps={{ 'aria-label': 'Address Line 1' }}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Address Line 2">
            <Controller
                name="address_line2"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        placeholder="Address line 2"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.address_line2}
                        helperText={errors.address_line2?.message}
                        inputProps={{ 'aria-label': 'Address Line 2' }}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Country" required>
            <Controller
                name="country"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.country} sx={adminFormControlSx}>
                        <Select {...field} displayEmpty inputProps={{ 'aria-label': 'Country' }}>
                            {countries.length === 0 ? (
                                <MenuItem value="" disabled>No Countries Available</MenuItem>
                            ) : (
                                countries.map((country) => <MenuItem key={country} value={country}>{country}</MenuItem>)
                            )}
                        </Select>
                        {errors.country && <FormHelperText>{errors.country.message}</FormHelperText>}
                    </FormControl>
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="State" required>
            <Controller
                name="state"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.state} sx={adminFormControlSx}>
                        <Select {...field} displayEmpty disabled={!selectedCountry} inputProps={{ 'aria-label': 'State' }}>
                            {selectedCountry && (statesByCountry[selectedCountry] || []).length > 0 ? (
                                (statesByCountry[selectedCountry] || []).map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)
                            ) : (
                                <MenuItem value="" disabled>{selectedCountry ? 'No States Available' : 'Select country first'}</MenuItem>
                            )}
                        </Select>
                        {errors.state && <FormHelperText>{errors.state.message}</FormHelperText>}
                    </FormControl>
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="City" required>
            <Controller
                name="city"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        placeholder="City"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.city}
                        helperText={errors.city?.message}
                        inputProps={{ 'aria-label': 'City' }}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Postal Code" required>
            <Controller
                name="postal_code"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        placeholder="Postal code"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.postal_code}
                        helperText={errors.postal_code?.message}
                        inputProps={{ 'aria-label': 'Postal Code' }}
                    />
                )}
            />
        </AdminFormRow>
        <Divider sx={adminCombineSx(adminDividerSx, { my: 0.5 })} />
        <AdminSectionHeading>Settings</AdminSectionHeading>
        <AdminFormRow label="LottoReco POS">
            <Controller
                name="cash_register_enabled"
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        sx={adminInlineControlLabelSx}
                        control={(
                            <Checkbox
                                size="small"
                                checked={field.value}
                                sx={adminCheckboxSx}
                                onChange={(event) => field.onChange(event.target.checked)}
                                inputProps={{ 'aria-label': 'Enable LottoReco POS' }}
                            />
                        )}
                        label="Enable LottoReco POS cash register"
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="POS Type" required>
            <Controller
                name="pos_type"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.pos_type} sx={adminFormControlSx}>
                        <Select {...field} displayEmpty inputProps={{ 'aria-label': 'POS Type' }}>
                            {posTypes.length === 0 ? (
                                <MenuItem value="" disabled>No POS Types Available</MenuItem>
                            ) : (
                                posTypes.map((type) => <MenuItem key={type} value={type}>{formatPosTypeLabel(type)}</MenuItem>)
                            )}
                        </Select>
                        {errors.pos_type && <FormHelperText>{errors.pos_type.message}</FormHelperText>}
                    </FormControl>
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Counting Direction" required>
            <Controller
                name="direction"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.direction} sx={adminFormControlSx}>
                        <Select {...field} inputProps={{ 'aria-label': 'Counting Direction' }}>
                            <MenuItem value="forwards">Forwards</MenuItem>
                            <MenuItem value="backwards">Backwards</MenuItem>
                        </Select>
                        {errors.direction && <FormHelperText>{errors.direction.message}</FormHelperText>}
                    </FormControl>
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Store Closing Time" required>
            <Controller
                name="store_closing_time"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        type="time"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.store_closing_time}
                        helperText={errors.store_closing_time?.message}
                        inputProps={{ 'aria-label': 'Store Closing Time', step: 60 }}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Backstock Audits">
            <Controller
                name="backstock_audit_frequency_seconds"
                control={control}
                render={({ field }) => (
                    <Stack spacing={0.5}>
                        <FormControlLabel
                            sx={adminInlineControlLabelSx}
                            control={(
                                <Checkbox
                                    checked={field.value !== null}
                                    onChange={(event) => field.onChange(
                                        event.target.checked
                                            ? DEFAULT_BACKSTOCK_AUDIT_FREQUENCY_SECONDS
                                            : null,
                                    )}
                                    inputProps={{ 'aria-label': 'Enable Backstock Audits' }}
                                    sx={adminCheckboxSx}
                                />
                            )}
                            label="Enable Backstock Audits"
                        />
                        <TextField
                            {...field}
                            value={field.value ?? ''}
                            disabled={field.value === null}
                            fullWidth
                            type="number"
                            size="small"
                            sx={adminFormFieldSx}
                            error={!!errors.backstock_audit_frequency_seconds}
                            helperText={
                                errors.backstock_audit_frequency_seconds?.message
                                || 'Seconds between audits. Defaults to 86400 (24 hours).'
                            }
                            inputProps={{
                                'aria-label': 'Backstock Audit Frequency',
                                min: 1,
                                step: 1,
                            }}
                            onChange={(event) => {
                                const { value } = event.target;
                                field.onChange(value === '' ? null : Number(value));
                            }}
                        />
                    </Stack>
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Scratcher Cutoff Offset" required>
            <Controller
                name="scratcher_reconciliation_cutoff_offset_minutes"
                control={control}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        type="number"
                        size="small"
                        sx={adminFormFieldSx}
                        error={!!errors.scratcher_reconciliation_cutoff_offset_minutes}
                        helperText={
                            errors.scratcher_reconciliation_cutoff_offset_minutes?.message
                            || 'Minutes from midnight. Use 180 for 3:00 AM or -60 for 11:00 PM.'
                        }
                        inputProps={{
                            'aria-label': 'Scratcher Cutoff Offset',
                            step: 1,
                        }}
                        onChange={(event) => {
                            const { value } = event.target;
                            field.onChange(value === '' ? '' : Number(value));
                        }}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Lotto Enabled">
            <Controller
                name="lotto_authorized"
                control={control}
                render={({ field }) => (
                    <FormControl fullWidth size="small" error={!!errors.lotto_authorized} sx={adminFormControlSx}>
                        <Select
                            {...field}
                            value={field.value ? 'true' : 'false'}
                            onChange={(event) => field.onChange(event.target.value === 'true')}
                            inputProps={{ 'aria-label': 'Lotto Enabled' }}
                        >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                        </Select>
                        {errors.lotto_authorized && <FormHelperText>{errors.lotto_authorized.message}</FormHelperText>}
                    </FormControl>
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Audit Confirmation Summary">
            <Controller
                name="enable_audit_confirmation_summary"
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        sx={adminInlineControlLabelSx}
                        control={(
                            <Checkbox
                                size="small"
                                checked={field.value}
                                sx={adminCheckboxSx}
                                onChange={(event) => field.onChange(event.target.checked)}
                            />
                        )}
                        label="Show audit variance summary after mobile audit submit"
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Mismatch Note">
            <Controller
                name="require_audit_mismatch_note"
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        sx={adminInlineControlLabelSx}
                        control={(
                            <Checkbox
                                size="small"
                                checked={field.value}
                                sx={adminCheckboxSx}
                                onChange={(event) => field.onChange(event.target.checked)}
                            />
                        )}
                        label="Require note for mismatched audits"
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Counter Audit Methods" required>
            <Controller
                name="scanning_methods.counter"
                control={control}
                render={({ field }) => {
                    const selectedMethods = Array.isArray(field.value) ? field.value : [];
                    return (
                        <FormControl
                            fullWidth
                            error={!!errors.scanning_methods?.counter}
                            sx={adminFormControlSx}
                        >
                            <FormGroup row sx={{ gap: 1.25 }}>
                                {COUNTER_SCANNING_METHOD_OPTIONS.map((option) => (
                                    <FormControlLabel
                                        key={option.key}
                                        sx={adminInlineControlLabelSx}
                                        control={(
                                            <Checkbox
                                                size="small"
                                                checked={selectedMethods.includes(option.key)}
                                                sx={adminCheckboxSx}
                                                onChange={(event) => {
                                                    if (event.target.checked) {
                                                        field.onChange([...selectedMethods, option.key]);
                                                        return;
                                                    }
                                                    field.onChange(
                                                        selectedMethods.filter((method) => method !== option.key)
                                                    );
                                                }}
                                            />
                                        )}
                                        label={option.label}
                                    />
                                ))}
                            </FormGroup>
                            {errors.scanning_methods?.counter && (
                                <FormHelperText>{errors.scanning_methods.counter.message}</FormHelperText>
                            )}
                        </FormControl>
                    );
                }}
            />
        </AdminFormRow>
        <Divider sx={adminCombineSx(adminDividerSx, { my: 0.5 })} />
        <AdminSectionHeading>POS Payout & Lotto Mapping</AdminSectionHeading>
        <AdminFormRow label="Payout Financial Event">
            <Controller
                name="pos_payout_mapping.financial_event"
                control={control}
                render={({ field }) => (
                    <AdminMultiValueAutocomplete
                        ariaLabel="Payout Financial Event"
                        dataOptions={mappingOptions.financial_event}
                        error={!!errors?.pos_payout_mapping?.financial_event}
                        helperText={errors?.pos_payout_mapping?.financial_event?.message}
                        inputRef={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        value={field.value}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Payout Transaction Line">
            <Controller
                name="pos_payout_mapping.transaction_line"
                control={control}
                render={({ field }) => (
                    <AdminMultiValueAutocomplete
                        ariaLabel="Payout Transaction Line"
                        dataOptions={mappingOptions.transaction_line}
                        error={!!errors?.pos_payout_mapping?.transaction_line}
                        helperText={errors?.pos_payout_mapping?.transaction_line?.message}
                        inputRef={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        value={field.value}
                    />
                )}
            />
        </AdminFormRow>
        <AdminFormRow label="Lotto Sales Transaction">
            <Controller
                name="pos_payout_mapping.lotto_merch_description"
                control={control}
                render={({ field }) => (
                    <AdminMultiValueAutocomplete
                        ariaLabel="Lotto Sales Transaction"
                        dataOptions={mappingOptions.lotto_merch_description}
                        error={!!errors?.pos_payout_mapping?.lotto_merch_description}
                        helperText={errors?.pos_payout_mapping?.lotto_merch_description?.message}
                        inputRef={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        value={field.value}
                    />
                )}
            />
        </AdminFormRow>
    </Stack>
);

export default React.memo(StoreDetailsStoreFormFields);
